/** @file prepareRig: indexes a puppet once (parents, twins, rest, IK chains, scratch), memoised per puppet. */
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import type { Puppet, PuppetNode } from '@zilo/pose3d-format/types/puppet';
import type { IkChain, Rig } from '@zilo/pose3d-pose/rig/rigTypes';
import type { LocalPose } from '@zilo/pose3d-pose/sample/localTypes';

const RIGS = new WeakMap<Puppet, Rig>();

function newLocals(n: number): LocalPose {
  return Array.from({ length: n }, () => ({
    rot: [0, 0, 0, 1], pos: [0, 0, 0], scale: 1, parent: -1, hidden: false, rotToggle: false, frameKey: 0,
  }));
}

/** Twin per node: `mirror` either way round (a one-sided `mirror` pairs both nodes), else self. */
function twinsOf(names: string[], nodes: PuppetNode[], index: Map<string, number>): Int32Array {
  const twin = Int32Array.from(names, (_, i) => i);
  nodes.forEach((node, i) => {
    const j = node.mirror === undefined ? undefined : index.get(node.mirror);
    if (j === undefined) return;
    twin[i] = j;
    if (twin[j] === j) twin[j] = i;
  });
  return twin;
}

/** Chain ending at `end`: `end` and its `ik - 1` ancestors, clamped so the top node has a parent (decision 15). */
function chainOf(end: number, ik: number, parent: Int32Array): IkChain | null {
  const nodes: number[] = [end];
  let top = end;
  while (nodes.length < ik && (parent[top] ?? -1) >= 0) {
    top = parent[top] ?? -1;
    nodes.unshift(top);
  }
  while (nodes.length > 0 && (parent[nodes[0] ?? -1] ?? -1) < 0) nodes.shift();
  const first = nodes[0];
  return first === undefined ? null : { end, nodes, pivot: parent[first] ?? -1 };
}

/** Own rest, else the nearest ancestor's along the default parents (18.2), else identity. */
function restOf(i: number, nodes: PuppetNode[], parent: Int32Array): readonly number[] {
  for (let k = i; k >= 0; k = parent[k] ?? -1) {
    const rest = nodes[k]?.rest;
    if (rest !== undefined) return quatFromYawPitchRoll(rest[0], rest[1], rest[2]);
  }
  return [0, 0, 0, 1];
}

function buildRig(puppet: Puppet): Rig {
  const names = Object.keys(puppet.nodes);
  const nodes = Object.values(puppet.nodes);
  const n = names.length;
  const index = new Map(names.map((name, i) => [name, i]));
  const parent = Int32Array.from(nodes, (node) => (node.parent === undefined ? -1 : (index.get(node.parent) ?? -1)));
  const chains = new Map<number, IkChain>();
  const restQuat = new Float64Array(n * 4);
  nodes.forEach((node, i) => {
    const chain = (node.ik ?? 0) > 0 ? chainOf(i, node.ik ?? 0, parent) : null;
    if (chain !== null) chains.set(i, chain);
    restQuat.set(restOf(i, nodes, parent), i * 4);
  });
  return {
    puppet, names, index, parent, twin: twinsOf(names, nodes, index),
    flex: Uint8Array.from(nodes, (node) => (node.flexParent === true ? 1 : 0)),
    hiddenDefault: Uint8Array.from(nodes, (node) => (node.hidden === true ? 1 : 0)),
    chains,
    pos: Float64Array.from(nodes.flatMap((node) => node.pos)),
    originOffset: Float64Array.from(nodes.flatMap((node) => node.originOffset ?? [0, 0, 0])),
    restQuat,
    frameAnim: nodes.map((node) => node.frameAnims?.default ?? null),
    workPos: new Float64Array(n * 3), workRot: new Float64Array(n * 4), workOrigin: new Float64Array(n * 3), workRoot: new Int32Array(n),
    workHostYaw: new Float64Array(n), workHostMove: new Float64Array(n * 3), workHostOn: new Uint8Array(n),
    workOrder: new Int32Array(n), workParent: new Int32Array(n), workMark: new Uint8Array(n), workStack: new Int32Array(n),
    workLocalsA: newLocals(n), workLocalsB: newLocals(n),
    workTimeA: { frame: 0, wraps: 0, elapsed: 0, ticks: 0 }, workTimeB: { frame: 0, wraps: 0, elapsed: 0, ticks: 0 },
  };
}

/** The puppet's rig (decisions 15, 19; 18.2, 18.6), built once per puppet object and memoised. */
export function prepareRig(puppet: Puppet): Rig {
  let rig = RIGS.get(puppet);
  if (rig === undefined) {
    rig = buildRig(puppet);
    RIGS.set(puppet, rig);
  }
  return rig;
}
